"use client";

import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import { useAuth } from "@/contexts/AuthContext";
import { useEntitlement } from "@/lib/auth/useEntitlement";
import AuthModal from "./AuthModal";

interface UserButtonProps {
  /** Optional — opens the in-app saved-plots panel. Only meaningful inside
   *  /app; when omitted the "Saved plots" item links to /account/plots so
   *  the same component can be reused in the marketing SiteNav. */
  onOpenSavedPlots?: () => void;
  /** Visual language of the trigger + dropdown.
   *  "hud"  (default) — glassy floating-panel styling for the /app map HUD.
   *  "site" — editorial styling (brand surfaces, hairlines, softer radii)
   *           matching SiteNav / the landing pages, so the desktop account
   *           menu reads as part of the same system as the mobile panel. */
  variant?: "hud" | "site";
}

export default function UserButton({ onOpenSavedPlots, variant = "hud" }: UserButtonProps = {}) {
  const { user, loading, signOut, configured } = useAuth();
  const entitlement = useEntitlement();
  const [showModal, setShowModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDropdown]);

  if (loading || !configured) return null;

  // Get user initials or first letter
  const getInitials = () => {
    if (!user) return "";
    const name = user.user_metadata?.full_name || user.email || "";
    if (user.user_metadata?.full_name) {
      const parts = name.split(" ");
      return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
    }
    return name[0]?.toUpperCase() || "?";
  };

  const avatarUrl = user?.user_metadata?.avatar_url;

  // Per-variant chrome. Content is identical — only the surface language
  // changes: "hud" floats over the map, "site" sits in the editorial pages.
  const chrome =
    variant === "site"
      ? {
          trigger:
            "w-9 h-9 rounded-md bg-brand-moss/30 border border-brand-border hover:border-brand-meadow/50 transition-all flex items-center justify-center overflow-hidden",
          signInTrigger:
            "flex items-center gap-2 px-3 py-2 rounded-md bg-brand-moss/30 border border-brand-border hover:border-brand-meadow/50 transition-all text-sm text-brand-sage hover:text-brand-linen",
          panel:
            "absolute right-0 top-full mt-2 w-64 bg-brand-pine border border-brand-border rounded-lg shadow-2xl overflow-hidden animate-[card-enter_0.2s_ease-out]",
          divider: "border-brand-border",
          dividerSoft: "border-brand-border/60",
        }
      : {
          trigger:
            "w-9 h-9 rounded-xl bg-panel-bg/80 backdrop-blur-xl border border-panel-border/60 hover:border-brand-border transition-all flex items-center justify-center overflow-hidden",
          signInTrigger:
            "flex items-center gap-2 px-3 py-2 rounded-xl bg-panel-bg/80 backdrop-blur-xl border border-panel-border/60 hover:border-brand-border transition-all text-sm text-brand-sage hover:text-brand-linen",
          panel:
            "absolute right-0 top-full mt-2 w-64 bg-panel-bg border border-panel-border rounded-xl shadow-2xl overflow-hidden animate-[card-enter_0.2s_ease-out]",
          divider: "border-panel-border",
          dividerSoft: "border-panel-border/60",
        };

  const itemClass =
    "w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-brand-sage hover:text-brand-linen hover:bg-brand-linen/5 transition-colors";

  if (!user) {
    return (
      <>
        <button onClick={() => setShowModal(true)} className={chrome.signInTrigger}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="8" cy="5.5" r="2.5" />
            <path d="M3 14c0-2.76 2.24-5 5-5s5 2.24 5 5" />
          </svg>
          <span className="text-xs font-medium">Sign in</span>
        </button>
        <AuthModal isOpen={showModal} onClose={() => setShowModal(false)} />
      </>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button onClick={() => setShowDropdown(!showDropdown)} className={chrome.trigger}>
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <span className="text-xs font-bold text-brand-meadow">{getInitials()}</span>
        )}
      </button>

      {showDropdown && (
        <div className={chrome.panel}>
          {/* User info */}
          <div className={`px-4 py-3 border-b ${chrome.divider}`}>
            <p className="text-xs font-medium text-brand-linen truncate">
              {user.user_metadata?.full_name || "User"}
            </p>
            <p className="text-[10px] text-brand-muted truncate">{user.email}</p>
          </div>

          {/* Plan status — editorial section header style */}
          <div className={`px-4 py-3 border-b ${chrome.dividerSoft}`}>
            <p className="text-[9px] text-brand-muted uppercase tracking-[0.22em] font-semibold mb-2">
              Plan
            </p>
            <PlanSummary entitlement={entitlement} />
          </div>

          {/* Actions — same set and order as the account rail and the mobile
              nav panel: Dashboard, Saved plots, Profile, Billing, Sign out. */}
          <div className="py-1">
            <Link
              href="/account/dashboard"
              onClick={() => setShowDropdown(false)}
              className={itemClass}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="1.5" y="1.5" width="5" height="5" rx="0.5" />
                <rect x="7.5" y="1.5" width="5" height="3" rx="0.5" />
                <rect x="7.5" y="5.5" width="5" height="7" rx="0.5" />
                <rect x="1.5" y="7.5" width="5" height="5" rx="0.5" />
              </svg>
              Dashboard
            </Link>
            {onOpenSavedPlots ? (
              <button
                onClick={() => {
                  setShowDropdown(false);
                  onOpenSavedPlots();
                }}
                className={itemClass}
              >
                <SavedPlotsIcon />
                Saved plots
              </button>
            ) : (
              <Link
                href="/account/plots"
                onClick={() => setShowDropdown(false)}
                className={itemClass}
              >
                <SavedPlotsIcon />
                Saved plots
              </Link>
            )}
            <Link
              href="/account"
              onClick={() => setShowDropdown(false)}
              className={itemClass}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="7" cy="5" r="2.5" />
                <path d="M2 12c0-2.5 2.24-4.5 5-4.5s5 2 5 4.5" />
              </svg>
              Profile
            </Link>
            <Link
              href="/account/billing"
              onClick={() => setShowDropdown(false)}
              className={itemClass}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <rect x="1.5" y="3.5" width="11" height="7" rx="1" />
                <path d="M1.5 6h11" />
              </svg>
              Billing
            </Link>
            <button
              onClick={() => {
                setShowDropdown(false);
                signOut();
              }}
              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-xs text-brand-sage hover:text-negative hover:bg-brand-linen/5 transition-colors border-t ${chrome.dividerSoft}`}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M5 12.5H2.5V1.5H5M9 3.5L12.5 7L9 10.5M5 7H12" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SavedPlotsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 3.5C2 2.67 2.67 2 3.5 2H5l1.5 1.5h4c.83 0 1.5.67 1.5 1.5v6c0 .83-.67 1.5-1.5 1.5h-7C2.67 12.5 2 11.83 2 11V3.5z" />
    </svg>
  );
}

/* ── Plan summary shown inside the dropdown ──────────────────────────── */

function PlanSummary({ entitlement }: { entitlement: ReturnType<typeof useEntitlement> }) {
  const { plan, freeScoresUsed, freeScoresRemaining, validUntil, loading } = entitlement;

  if (loading || plan === null) {
    return <p className="text-[11px] italic text-brand-muted" style={{ fontFamily: "var(--font-display), Georgia, serif" }}>Loading…</p>;
  }

  if (plan === "free") {
    const remaining = freeScoresRemaining ?? 0;
    const nudge = remaining === 0;
    return (
      <div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-semibold text-brand-linen">Free</span>
          <span className={`font-mono text-[12px] tabular-nums ${nudge ? "text-brand-sand" : remaining === 1 ? "text-warning" : "text-brand-sage"}`}>
            {freeScoresUsed} / 3
          </span>
        </div>
        <p
          className="text-[11px] italic mt-1 leading-snug"
          style={{ fontFamily: "var(--font-display), Georgia, serif", color: nudge ? "rgb(var(--c-sand))" : "rgb(var(--c-ink-muted))" }}
        >
          {nudge
            ? "You've used all three. The next click unlocks the Pass."
            : remaining === 1
              ? "One free score left, then the pass unlocks unlimited."
              : "Three free scores, then €39 for 30 days unlimited."}
        </p>
      </div>
    );
  }

  if (plan === "pass") {
    const days = daysUntil(validUntil);
    const urgent = days !== null && days <= 3;
    return (
      <div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-semibold text-brand-sand">Buyer's Pass</span>
          <span className={`font-mono text-[12px] tabular-nums ${urgent ? "text-warning" : "text-brand-sand/80"}`}>
            {days === null ? "—" : days <= 0 ? "today" : `${days}d`}
          </span>
        </div>
        <p
          className="text-[11px] italic mt-1 leading-snug"
          style={{ fontFamily: "var(--font-display), Georgia, serif", color: urgent ? "#fbbf24" : "rgb(var(--c-ink-muted))" }}
        >
          {urgent
            ? `Expires soon — renew anytime from billing.`
            : validUntil
              ? `Unlimited through ${validUntil.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}.`
              : `Unlimited scoring, this month.`}
        </p>
      </div>
    );
  }

  const planLabel = plan === "pro" ? "Pro" : "Studio";
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-semibold text-brand-meadow">{planLabel}</span>
        <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-brand-meadow/60">
          Active
        </span>
      </div>
      <p
        className="text-[11px] italic mt-1 leading-snug text-brand-muted"
        style={{ fontFamily: "var(--font-display), Georgia, serif" }}
      >
        {validUntil
          ? `Renews ${validUntil.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}.`
          : "Active subscription, renewing automatically."}
      </p>
    </div>
  );
}

function daysUntil(date: Date | null): number | null {
  if (!date) return null;
  const diffMs = date.getTime() - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}
